import { useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const usd = (v, dp = 2) => (v === null || v === undefined ? "—" : `US$${Number(v).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })}`);
const AX = { tick: { fontSize: 11, fill: "#64748b" } };
const SERIES = [["ingest", "Ingesting & checking", "#0f766e"], ["screening", "Screening", "#b45309"], ["other", "Other AI", "#64748b"], ["email", "Email", "#7c3aed"]];
const COLS = [["today", "Today"], ["this_month", "This month to date"], ["last_month", "Last month"], ["all_time", "Since tracking began"], ["forecast", "Forecast next month"]];

export const SpendTable = ({ s }) => (
  <div className="overflow-x-auto" data-testid="spend-table">
    <table className="w-full min-w-[720px] text-left text-sm">
      <thead className="text-xs text-slate-500"><tr><th className="py-1 pr-3">Item</th>{COLS.map(([k, l]) => <th key={k} className={`pr-3 text-right ${k === "forecast" ? "text-slate-900" : ""}`}>{l}</th>)}</tr></thead>
      <tbody>
        {s.rows.map((r) => (
          <tr key={r.key} className="border-t border-border align-top" data-testid={`spend-row-${r.key}`}>
            <td className="py-2 pr-3"><p className="font-medium">{r.label}</p><p className="text-xs text-slate-500">{r.note}</p></td>
            {COLS.map(([k]) => <td key={k} className={`pr-3 text-right tabular-nums ${k === "forecast" ? "font-semibold" : ""}`}>{usd(r[k], k === "today" ? 3 : 2)}</td>)}
          </tr>
        ))}
        <tr className="border-t-2 border-slate-900 font-semibold" data-testid="spend-row-total">
          <td className="py-2 pr-3">Total</td>{COLS.map(([k]) => <td key={k} className="pr-3 text-right tabular-nums">{usd(s.totals[k], k === "today" ? 3 : 2)}</td>)}
        </tr>
      </tbody>
    </table>
  </div>
);

export const SpendChart = ({ rows }) => (
  <ResponsiveContainer width="100%" height={200}>
    <BarChart data={rows} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
      <CartesianGrid stroke="#e2e8f0" vertical={false} />
      <XAxis dataKey="day" tickFormatter={(d) => d.slice(5)} minTickGap={20} {...AX} /><YAxis tickFormatter={(v) => `$${v}`} {...AX} />
      <Tooltip formatter={(v, n) => [`$${Number(v).toFixed(3)}`, SERIES.find((x) => x[0] === n)?.[1]]} />
      <Legend formatter={(n) => SERIES.find((x) => x[0] === n)?.[1]} wrapperStyle={{ fontSize: 12 }} />
      {SERIES.map(([k, , c]) => <Bar key={k} dataKey={k} stackId="a" fill={c} maxBarSize={28} isAnimationActive={false} />)}
    </BarChart>
  </ResponsiveContainer>
);

export const PlatformCosts = ({ platform, onSave }) => {
  const [items, setItems] = useState(platform.items.length ? platform.items : [{ name: "Hosting & deployment", monthly: "" }]);
  const [email, setEmail] = useState(platform.email_price);
  const set = (i, k, v) => setItems(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  return (
    <div className="space-y-3" data-testid="platform-costs">
      <h3 className="text-lg">Emergent fixed monthly costs</h3>
      <p className="text-xs text-slate-500">The app can't read your Emergent bill. Enter fixed monthly costs (such as hosting and deployment) in US dollars. The app counts AI calls and emails by itself.</p>
      {items.map((it, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2">
          <Input className="h-9 w-56" value={it.name} placeholder="e.g. Hosting & deployment" onChange={(e) => set(i, "name", e.target.value)} data-testid={`platform-item-name-${i}`} />
          <Input className="h-9 w-32" type="number" min={0} step="0.01" value={it.monthly} placeholder="US$ / month" onChange={(e) => set(i, "monthly", e.target.value)} data-testid={`platform-item-monthly-${i}`} />
          <Button size="icon" variant="ghost" onClick={() => setItems(items.filter((_, j) => j !== i))} data-testid={`platform-item-remove-${i}`}><Trash2 size={15} /></Button>
        </div>
      ))}
      <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setItems([...items, { name: "", monthly: "" }])} data-testid="platform-item-add"><Plus size={14} className="mr-1" />Add cost</Button>
      <label className="flex flex-wrap items-center gap-2 text-sm">Estimated cost per email (US$)
        <Input className="h-9 w-28" type="number" min={0} step="0.0005" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="platform-email-price" /></label>
      <Button size="sm" className="rounded-full" onClick={() => onSave({ items: items.filter((x) => x.name.trim()).map((x) => ({ name: x.name, monthly: Number(x.monthly) || 0 })), email_price: Number(email) || 0 })} data-testid="platform-costs-save">Save platform costs</Button>
    </div>
  );
};
